/* ==========================================================================
   ZOMBIE SNACKS — THE ZOMBIE GAME: 30-SECOND ROUND (live game, 5 Oct 2026;
   built 2–3 Oct as the timed test). Loaded by sections/zombie-munch.liquid on
   every page that has the section (Home, About), so there is one game. It
   attaches to the <zombie-munch> element and reuses its art, drag / tap /
   keyboard throwing, eating, sounds and reduced-motion handling, through hooks
   in zombie-munch.js (eat, miss, replay, isRotten).

   Every fruit is one of four kinds — the player learns them by playing:
     GOOD   fresh fruit: +10 × combo;
     BONUS  golden fruit (gold glow + ×3 tag, never rotten): +30 × combo;
     WARN   a fresh fruit left too long shivers and dulls for 0.7s ("turning")
            — still fresh, last chance;
     BAD    then it swaps to the supplied rotten artwork and stays long enough
            to be recognised (≈3s early, ≈2s in frenzy) before it shrivels
            away. Rotten fruit can also appear on their own as the round
            heats up. Feeding one scores nothing and breaks the combo, with a
            rotten bite sound, a BLEURGH! and a brief "ROTTEN! -10" tag:
            10 points off (never below zero) and the combo is lost.
   Combo: ×2 after 3 good feeds in a row, ×3 after 6, ×4 after 9; a rotten
   feed or a fruit dropped short resets it.

   30s round, four phases (fractions of the round: 0–10 / 10–20 / 20–25 /
   25–30s): normal → faster → high pressure → FRENZY. Respawn delay, how long
   fresh fruit last, how often a respawn is rotten or golden, and how long
   rotten fruit linger all ease smoothly between the marks (no jumps, no
   flashing). Early on almost everything is fresh; rotten fruit appear now and
   then from 10s, more from 20s, and frenzy is a fast fresh / rotten / bonus
   mix. At most one rotten fruit on screen before 20s, two after, and never
   fewer than two good targets.

   Time's up: arcade score screen (final score, best score, best combo, fruit
   fed); best score persists in this browser only. A low score (< 300) gets
   the "low score" finish sound when supplied.

   Sounds (zombie-munch.liquid config, played by zombie-munch.js, which picks
   variations at random without repeats and stops close events piling up):
   pickup · eat (2 variations here) · zombie tap (3) · rotten bite (2) ·
   reward (combo up / bonus fruit) · finish · low-score finish.
   Sound is optional: everything reads without it. All timers run only while a
   round is live and are cleared on time-up, replay and disconnect.
   Off screen = paused: if the stage scrolls out of view or the tab is hidden
   mid-round (or mid-countdown), the clock, respawns and fruit timers freeze
   and nothing plays; the round carries on from the same moment when the
   stage is back in view (5 Oct 2026).
   ========================================================================== */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
  const smooth = (t) => t * t * (3 - 2 * t);
  const BEST_KEY = 'zombie-munch-timed-best';
  const LOW_SCORE = 300;
  const ROTTEN_PENALTY = 10; // feeding a rotten fruit: score = max(0, score - 10), combo resets
  const TURN_MS = 700; // the warning before a fresh fruit turns rotten

  // Pace marks (fraction of the round) and the values at each mark.
  const MARKS = [0, 1 / 3, 2 / 3, 5 / 6, 1];
  const PACE = {
    respawn: [900, 760, 540, 400, 300], // ms before an eaten fruit comes back
    fresh: [7000, 6000, 4600, 3700, 3200], // ms a fresh fruit waits before it starts turning
    rotten: [0, 0.03, 0.14, 0.24, 0.3], // chance a respawn arrives rotten
    golden: [0, 0.06, 0.1, 0.14, 0.18], // chance a respawn is a bonus fruit (×3)
    rottenLife: [3200, 3000, 2600, 2200, 1900] // ms a rotten fruit lingers before it shrivels away
  };
  // eased value of a pace curve at round progress p (0–1)
  const paceAt = (curve, p) => {
    const x = clamp(p, 0, 1);
    let i = 0;
    while (i < MARKS.length - 2 && x > MARKS[i + 1]) i += 1;
    const span = (x - MARKS[i]) / (MARKS[i + 1] - MARKS[i]);
    return curve[i] + (curve[i + 1] - curve[i]) * smooth(clamp(span, 0, 1));
  };
  // 0 normal, 1 faster, 2 high pressure, 3 frenzy
  const phaseAt = (p) => (p >= MARKS[3] ? 3 : p >= MARKS[2] ? 2 : p >= MARKS[1] ? 1 : 0);
  const digits = (n) => (n > 9999 ? String(n) : String(n).padStart(4, '0'));
  const title = (s) => s.charAt(0).toUpperCase() + s.slice(1);

  class TimedGame {
    constructor(munch) {
      this.m = munch;
      this.seconds = clamp(parseInt(munch.dataset.seconds, 10) || 30, 20, 45);
      const q = (selector) => munch.querySelector(selector);
      this.timeEl = q('[data-zmt-time]');
      this.scoreEl = q('[data-zmt-score]');
      this.comboEl = q('[data-zmt-combo]');
      this.comboCell = q('[data-zmt-combo-cell]');
      this.bestLiveEl = q('[data-zmt-best-live]');
      this.startPanel = q('[data-zmt-start]');
      this.countEl = q('[data-zmt-count]');
      this.overPanel = q('[data-zmt-over]');
      this.finalBoard = q('[data-zmt-final-board]');
      this.toastEl = q('[data-zmt-toast]');
      this.state = 'idle';
      this.phase = 0;
      this.waits = new Set(); // pausable timeouts (see wait / pause / resume)
      this.life = new Map();
      this.ticker = 0;
      this.paused = false;
      this.onScreen = true;

      // fruit art: { fresh, rotten } per fruit (rotten = the supplied artwork,
      // preloaded so the swap is instant) and the fruit's normal button label
      this.art = new Map();
      munch.fruits.forEach((fruit) => {
        const img = fruit.querySelector('img');
        const rotten = fruit.dataset.rottenSrc || null;
        if (rotten) { const preload = new Image(); preload.src = rotten; }
        this.art.set(fruit, { img, fresh: img ? img.getAttribute('src') : null, rotten, label: fruit.getAttribute('aria-label') || '' });
      });

      this.best = this.readBest();
      this.resetScore();

      munch.classList.add('zmt', 'zmt-waiting');
      this.setFruitsEnabled(false);
      const on = { signal: munch.abort.signal };
      q('[data-zmt-go]').addEventListener('click', () => this.start(), on);
      q('[data-zmt-again]').addEventListener('click', () => this.restart(), on);
      munch.abort.signal.addEventListener('abort', () => this.stopAll());

      // pause mid-round when the game can't be seen; resume where it left off
      const stage = q('[data-zm-stage]') || munch;
      this.io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => { this.onScreen = entry.isIntersecting && entry.intersectionRatio >= 0.25; });
        this.syncPause();
      }, { threshold: [0, 0.25, 0.5] });
      this.io.observe(stage);
      document.addEventListener('visibilitychange', () => this.syncPause(), on);
      munch.abort.signal.addEventListener('abort', () => this.io.disconnect());
    }

    syncPause() {
      if (this.onScreen && !document.hidden) this.resume();
      else this.pause();
    }

    pause() {
      if (this.paused || (this.state !== 'countdown' && this.state !== 'running')) return;
      this.paused = true;
      this.pausedAt = performance.now();
      clearInterval(this.ticker);
      this.ticker = 0;
      this.waits.forEach((h) => { clearTimeout(h.id); h.ms = Math.max(0, h.ms - (this.pausedAt - h.start)); });
      this.m.fruits.forEach((fruit) => fruit.getAnimations().forEach((animation) => animation.pause()));
      this.m.classList.add('zmt-paused');
    }

    resume() {
      if (!this.paused) return;
      this.paused = false;
      const now = performance.now();
      if (this.state === 'running') this.t0 += now - this.pausedAt;
      this.waits.forEach((h) => this.arm(h));
      this.m.fruits.forEach((fruit) => fruit.getAnimations().forEach((animation) => animation.play()));
      this.m.classList.remove('zmt-paused');
      if (this.state === 'running') this.ticker = setInterval(() => this.tick(), 100);
    }

    // a built-in gameplay word from snippets/zombie-munch-copy.liquid, {placeholders} filled in
    t(key, vars = {}) {
      let text = (this.m.copy && this.m.copy[key]) || '';
      Object.keys(vars).forEach((name) => { text = text.split(`{${name}}`).join(vars[name]); });
      return text;
    }

    readBest() {
      try { return parseInt(localStorage.getItem(BEST_KEY), 10) || 0; } catch (error) { return 0; }
    }

    // zombie-munch.js asks this when a fruit is eaten (rotten bite instead of a normal one)
    isRotten(fruit) {
      return fruit.classList.contains('zmt-rotten');
    }

    // ---- Round lifecycle --------------------------------------------------
    start() {
      if (this.state === 'countdown' || this.state === 'running') return;
      this.track('game_start', { replay: this.state === 'replay' });
      this.m.unlockAudio();
      this.m.started();
      this.state = 'countdown';
      this.startPanel.hidden = true;
      this.overPanel.hidden = true;
      if (this.finalBoard) this.finalBoard.hidden = true;
      this.m.classList.remove('zmt-over');
      this.resetScore();
      this.setPhase(0);
      this.timeEl.textContent = this.seconds;
      const steps = this.t('countdown').split(',').map((step) => step.trim()).filter(Boolean);
      steps.forEach((label, i) => this.later(() => this.showCount(label), i * 600));
      this.later(() => { this.countEl.hidden = true; this.begin(); }, steps.length * 600 - 150);
      this.m.announce(this.t('sr_get_ready', { s: this.seconds }));
      // small screens: bring the whole stage (every fruit) into view for the round
      const stage = this.m.querySelector('[data-zm-stage]');
      const box = stage ? stage.getBoundingClientRect() : null;
      if (box && (box.top < 0 || box.bottom > window.innerHeight)) stage.scrollIntoView({ block: 'nearest', behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      this.syncPause(); // started while mostly scrolled away: hold the countdown until it's in view
    }

    // GA4 (zombie-analytics.js listens for zombie:track on the document)
    track(name, params) {
      this.m.dispatchEvent(new CustomEvent('zombie:track', { bubbles: true, detail: { name, params: Object.assign({ game_name: 'feed_the_zombie' }, params) } }));
    }

    showCount(label) {
      this.countEl.hidden = false;
      this.countEl.textContent = label;
      if (!reduceMotion.matches) {
        this.countEl.animate([
          { transform: 'scale(1.5)', opacity: 0 },
          { transform: 'scale(1)', opacity: 1, offset: 0.35 },
          { transform: 'scale(0.92)', opacity: 0.9 }
        ], { duration: 560, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)' });
      }
    }

    begin() {
      this.state = 'running';
      this.t0 = performance.now();
      this.m.classList.remove('zmt-waiting');
      this.setFruitsEnabled(true);
      this.m.fruits.forEach((fruit) => { if (!fruit.hidden) this.armLife(fruit); });
      this.ticker = setInterval(() => this.tick(), 100);
      this.m.say(this.m.lines.idle);
    }

    tick() {
      if (this.state !== 'running') return;
      const elapsed = (performance.now() - this.t0) / 1000;
      const left = Math.max(0, this.seconds - elapsed);
      this.timeEl.textContent = Math.ceil(left);
      this.setPhase(phaseAt(elapsed / this.seconds));
      if (left <= 0) this.end();
    }

    setPhase(phase) {
      if (phase === this.phase && this.m.classList.contains(`zmt-phase-${phase}`)) return;
      const previous = this.phase;
      this.phase = phase;
      [0, 1, 2, 3].forEach((n) => this.m.classList.toggle(`zmt-phase-${n}`, n === phase));
      this.m.classList.toggle('zmt-frenzy', phase === 3);
      if (this.state !== 'running' || phase <= previous) return;
      const talk = !(this.holdBubbleUntil > performance.now()); // never talk over a BLEURGH!
      if (phase === 2) {
        if (talk) this.m.say(this.t('hurry'), 1400);
        this.m.announce(this.t('sr_hurry'));
      } else if (phase === 3) {
        if (talk) this.m.say(this.t('frenzy'), 1600);
        this.m.announce(this.t('sr_frenzy', { n: Math.ceil(this.seconds / 6) }));
      }
    }

    progress() {
      if (this.state !== 'running') return 0;
      return clamp((performance.now() - this.t0) / (this.seconds * 1000), 0, 1);
    }

    end() {
      this.state = 'over';
      this.stopAll();
      this.m.classList.add('zmt-waiting');
      this.setPhase(0);
      this.setFruitsEnabled(false);
      this.hideToast();
      this.timeEl.textContent = 0;
      const newBest = this.score > this.best;
      if (newBest) {
        this.best = this.score;
        try { localStorage.setItem(BEST_KEY, String(this.best)); } catch (error) { /* storage blocked */ }
      }
      this.renderScore();
      const set = (selector, value) => { const el = this.m.querySelector(selector); if (el) el.textContent = value; };
      set('[data-zmt-final]', digits(this.score));
      set('[data-zmt-best]', digits(this.best));
      set('[data-zmt-best-combo]', this.bestMult);
      set('[data-zmt-fed]', this.fed);
      const flag = this.m.querySelector('[data-zmt-newbest]');
      if (flag) flag.hidden = !newBest || this.score === 0;
      // the round finishes where it was played: results in the scoreboard's strip,
      // TIME'S UP + buttons under the chin, the leftover fruit fade away (CSS)
      this.overPanel.hidden = false;
      if (this.finalBoard) this.finalBoard.hidden = false;
      this.m.classList.add('zmt-over');
      if (!reduceMotion.matches) {
        this.overPanel.animate([
          { opacity: 0, transform: 'translateY(10px) scale(0.92)' },
          { opacity: 1, transform: 'none' }
        ], { duration: 380, easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)' });
      }
      // the result: a low score gets the sorry-sounding finish when it is supplied
      if (this.score < LOW_SCORE && this.m.hasSample('timeupLow')) this.m.sfx('timeupLow', 0.1);
      else this.m.sfx('finale', 0.1);
      this.track('game_over', { score: this.score, best_score: this.best, new_best: newBest && this.score > 0, fruit_fed: this.fed, best_combo: this.bestMult, duration_sec: this.seconds });
      this.m.announce(this.t('sr_time_up', { score: this.score, newbest: newBest && this.score ? this.t('sr_new_best') : '', best: this.best, fed: this.fed, combo: this.bestMult }));
      const again = this.m.querySelector('[data-zmt-again]');
      if (again) again.focus({ preventScroll: true });
      // bring TIME'S UP + buttons into view if the stage was scrolled past them
      const box = this.overPanel.getBoundingClientRect();
      if (box.top < 0 || box.bottom > window.innerHeight) this.overPanel.scrollIntoView({ block: 'nearest', behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }

    restart() {
      this.stopAll();
      this.state = 'replay';
      this.m.reset();
      this.m.hideHand();
      this.m.interacted = true;
      this.m.fruits.forEach((fruit) => this.freshen(fruit));
      this.hideToast();
      this.start();
    }

    stopAll() {
      clearInterval(this.ticker);
      this.ticker = 0;
      this.waits.forEach((h) => clearTimeout(h.id));
      this.waits.clear();
      this.life.clear();
      this.paused = false;
      this.m.classList.remove('zmt-paused');
    }

    // a timeout that freezes while the game is paused (off screen / tab hidden)
    wait(fn, ms) {
      const h = { fn, ms };
      this.waits.add(h);
      if (!this.paused) this.arm(h);
      return h;
    }

    arm(h) {
      h.start = performance.now();
      h.id = setTimeout(() => { this.waits.delete(h); h.fn(); }, h.ms);
    }

    cancel(h) {
      if (!h) return;
      clearTimeout(h.id);
      this.waits.delete(h);
    }

    later(fn, ms) {
      this.wait(fn, ms);
    }

    // ---- Scoring + scoreboard ---------------------------------------------
    resetScore() {
      this.score = 0;
      this.combo = 0;
      this.mult = 1;
      this.bestMult = 1;
      this.fed = 0;
      this.rottenFed = 0;
      this.renderScore();
    }

    renderScore() {
      this.scoreEl.textContent = digits(this.score);
      this.comboEl.textContent = this.mult;
      if (this.comboCell) this.comboCell.classList.toggle('is-hot', this.mult > 1);
      if (this.bestLiveEl) {
        this.bestLiveEl.textContent = digits(Math.max(this.best, this.score));
        this.bestLiveEl.parentElement.classList.toggle('is-hot', this.state === 'running' && this.score > this.best);
      }
    }

    onEat(fruit, hadFocus) {
      this.cancel(this.life.get(fruit));
      this.life.delete(fruit);
      if (this.state !== 'running') return;
      if (this.isRotten(fruit)) this.onRottenEat(fruit);
      else this.onGoodEat(fruit);
      this.respawn(fruit, paceAt(PACE.respawn, this.progress()));
      if (hadFocus) {
        const next = this.m.fruits.find((other) => !other.hidden && !other.dataset.state);
        if (next) next.focus({ preventScroll: true });
      }
    }

    onGoodEat(fruit) {
      const golden = fruit.classList.contains('zmt-golden');
      this.combo += 1;
      const mult = 1 + Math.min(3, Math.floor(this.combo / 3));
      const levelledUp = mult > this.mult;
      this.mult = mult;
      this.bestMult = Math.max(this.bestMult, mult);
      this.score += 10 * (golden ? 3 : 1) * mult;
      this.fed += 1;
      this.renderScore();
      // one reward sound even when both happen, landing just after the crunch
      if (golden || levelledUp) this.m.sfx('reward', 0.42);
      if (golden) this.toast(this.t('bonus'), 'bonus');
      if (levelledUp) {
        if (!golden) this.toast(this.t('combo_up', { n: mult }), 'combo');
        this.m.say(this.t('combo_up', { n: mult }), 1400);
        this.m.announce(this.t('sr_combo_up', { n: mult }));
        if (!reduceMotion.matches && this.comboCell) {
          this.comboCell.animate([{ transform: 'scale(1.18)' }, { transform: 'none' }], { duration: 320, easing: 'ease-out' });
        }
      }
    }

    // BAD: no points, combo gone, a short gross-out — never a deduction
    // rotten is still edible, but it costs: −10 points (never below zero) and the combo
    onRottenEat(fruit) {
      const lost = this.combo > 0;
      const penalty = Math.min(ROTTEN_PENALTY, this.score);
      this.rottenFed += 1;
      this.score = Math.max(0, this.score - ROTTEN_PENALTY);
      this.combo = 0;
      this.mult = 1;
      this.renderScore();
      this.toast(penalty ? this.t('rotten_toast', { n: penalty }) : this.t('rotten_toast_zero'), 'bad');
      this.m.say(this.t('rotten_word'), 1400);
      this.holdBubbleUntil = performance.now() + 1400; // a quick good bite won't talk over it
      this.m.announce(`${this.t(penalty ? 'sr_rotten' : 'sr_rotten_zero', { fruit: fruit.dataset.name, n: penalty })}${lost ? this.t('sr_combo_lost') : ''}.`);
      if (!reduceMotion.matches && this.comboCell && lost) {
        this.comboCell.animate([
          { transform: 'translateX(0)' }, { transform: 'translateX(-4px)' }, { transform: 'translateX(4px)' },
          { transform: 'translateX(-2px)' }, { transform: 'none' }
        ], { duration: 320, easing: 'ease-out' });
      }
    }

    onMiss() {
      if (this.state === 'running') this.breakCombo();
    }

    breakCombo() {
      if (this.combo === 0) return;
      this.combo = 0;
      this.mult = 1;
      this.renderScore();
    }

    // brief tag under the scoreboard (top of the stage, clear of the face)
    toast(text, kind) {
      if (!this.toastEl) return;
      clearTimeout(this.toastTimer);
      this.toastEl.textContent = text;
      this.toastEl.className = `zmt-toast zmt-toast--${kind}`;
      this.toastEl.hidden = false;
      if (!reduceMotion.matches) {
        this.toastEl.animate([
          { opacity: 0, transform: 'translate(-50%, -6px) scale(0.9)' },
          { opacity: 1, transform: 'translate(-50%, 0) scale(1)' }
        ], { duration: 180, easing: 'ease-out' });
      }
      this.toastTimer = setTimeout(() => this.hideToast(), kind === 'bad' ? 1300 : 1000);
    }

    hideToast() {
      clearTimeout(this.toastTimer);
      if (this.toastEl) this.toastEl.hidden = true;
    }

    // ---- Fruit kinds: fresh → turning → rotten, golden, respawn ------------------
    setArt(fruit, kind) {
      const art = this.art.get(fruit);
      if (!art || !art.img) return;
      const src = kind === 'rotten' && art.rotten ? art.rotten : art.fresh;
      if (art.img.getAttribute('src') !== src) art.img.setAttribute('src', src);
      fruit.setAttribute('aria-label', kind === 'rotten'
        ? this.t('rotten_label', { fruit: fruit.dataset.name, n: ROTTEN_PENALTY })
        : art.label);
    }

    freshen(fruit) {
      fruit.classList.remove('zmt-golden', 'zmt-turning', 'zmt-rotten', 'zmt-rotting', 'zmt-rotten-art');
      this.setArt(fruit, 'fresh');
    }

    visible(test) {
      return this.m.fruits.filter((fruit) => !fruit.hidden && !fruit.dataset.state && test(fruit)).length;
    }

    respawn(fruit, delay) {
      this.later(() => {
        if (this.state !== 'running') return;
        fruit.getAnimations().forEach((animation) => animation.cancel());
        fruit.classList.remove('is-held', 'is-returning');
        this.freshen(fruit);
        fruit.style.transform = '';
        fruit.zmOffset = null;
        delete fruit.dataset.state;
        const p = this.progress();
        const maxRotten = this.phase >= 2 ? 2 : 1;
        const rotten = Math.random() < paceAt(PACE.rotten, p)
          && this.visible((f) => this.isRotten(f)) < maxRotten
          && this.visible((f) => !this.isRotten(f)) >= 2;
        const golden = !rotten && Math.random() < paceAt(PACE.golden, p);
        if (rotten) this.makeRotten(fruit, true);
        fruit.classList.toggle('zmt-golden', golden);
        fruit.hidden = false;
        fruit.disabled = false;
        if (!reduceMotion.matches) {
          fruit.animate([{ transform: 'scale(0.2)', opacity: 0 }, { transform: 'scale(1.12)', opacity: 1, offset: 0.7 }, { transform: 'scale(1)' }],
            { duration: 320, easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)' });
        }
        if (!rotten) this.armLife(fruit);
      }, delay);
    }

    // a fresh fruit lasts a while, then turns; a bonus fruit just vanishes if ignored
    armLife(fruit) {
      this.cancel(this.life.get(fruit));
      const fresh = paceAt(PACE.fresh, this.progress());
      if (fruit.classList.contains('zmt-golden')) this.life.set(fruit, this.wait(() => this.expire(fruit), fresh * 0.75));
      else this.life.set(fruit, this.wait(() => this.turn(fruit), fresh));
    }

    // held or mid-flight: try again shortly
    busy(fruit) {
      return Boolean(fruit.dataset.state || (this.m.drag && this.m.drag.fruit === fruit));
    }

    // WARN: shiver + dull for a moment (still fresh), then rot
    turn(fruit) {
      this.life.delete(fruit);
      if (this.state !== 'running' || fruit.hidden) return;
      if (this.busy(fruit)) { this.life.set(fruit, this.wait(() => this.turn(fruit), 600)); return; }
      fruit.classList.add('zmt-turning');
      this.life.set(fruit, this.wait(() => {
        this.life.delete(fruit);
        if (this.state !== 'running' || fruit.hidden || fruit.dataset.state === 'eaten') return;
        this.makeRotten(fruit, false);
      }, reduceMotion.matches ? 400 : TURN_MS));
    }

    // BAD: the supplied rotten artwork, then it shrivels away after a recognisable moment
    makeRotten(fruit, arriving) {
      fruit.classList.remove('zmt-turning', 'zmt-golden');
      fruit.classList.add('zmt-rotten');
      this.setArt(fruit, 'rotten');
      if (!arriving && !reduceMotion.matches) {
        const art = fruit.querySelector('.zm-fruit__art');
        if (art) art.animate([{ transform: 'scale(1.12)' }, { transform: 'scale(0.96)', offset: 0.6 }, { transform: 'none' }], { duration: 320, easing: 'ease-out' });
      }
      this.cancel(this.life.get(fruit));
      this.life.set(fruit, this.wait(() => this.expire(fruit), paceAt(PACE.rottenLife, this.progress())));
    }

    expire(fruit) {
      this.life.delete(fruit);
      if (this.state !== 'running' || fruit.hidden) return;
      if (this.busy(fruit)) { this.life.set(fruit, this.wait(() => this.expire(fruit), 600)); return; }
      fruit.dataset.state = 'expiring';
      const done = () => { fruit.hidden = true; this.respawn(fruit, paceAt(PACE.respawn, this.progress())); };
      if (reduceMotion.matches) { done(); return; }
      const anim = fruit.animate([
        { transform: 'scale(1)', opacity: 1 },
        { transform: 'scale(0.6) translateY(12%)', opacity: 0 }
      ], { duration: 450, easing: 'ease-in', fill: 'forwards' });
      anim.onfinish = done;
    }

    // zombie-munch.js asks this when a fruit that is shrinking away gets grabbed:
    // the grab wins (cancel the shrink, give it a short new life) so a fruit the
    // player can still see is always catchable, rotten or not
    rescue(fruit) {
      if (this.state !== 'running' || fruit.dataset.state !== 'expiring') return false;
      fruit.getAnimations().forEach((animation) => animation.cancel()); // onfinish never fires
      delete fruit.dataset.state;
      this.cancel(this.life.get(fruit));
      this.life.set(fruit, this.wait(() => this.expire(fruit), 1200));
      return true;
    }

    setFruitsEnabled(enabled) {
      this.m.fruits.forEach((fruit) => { fruit.disabled = !enabled; });
    }
  }

  const attach = () => {
    document.querySelectorAll('zombie-munch').forEach((munch) => {
      if (munch.game || !munch.abort) return;
      munch.game = new TimedGame(munch);
    });
  };
  if (customElements.get('zombie-munch')) attach();
  else customElements.whenDefined('zombie-munch').then(attach);
  document.addEventListener('shopify:section:load', attach);
})();
